import { useRef, useState } from "react";
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Mark } from "@/components/Mark";
import { Reveal } from "@/components/Reveal";

const STAGES = [
  { label: "Una idea", desc: "«Quiero algo así.»" },
  { label: "Interpretación", desc: "El profesional empieza a traducir la intención.", mark: true },
  { label: "Decisiones" },
  { label: "Un proyecto que puede existir" },
];

function Stage({ progress, index, stage }) {
  const start = index * 0.22;
  const isLast = index === STAGES.length - 1;
  const opacity = useTransform(
    progress,
    isLast ? [start, start + 0.12] : [start, start + 0.12, start + 0.3, start + 0.46],
    isLast ? [0.14, 1] : [0.14, 1, 1, 0.4]
  );
  const y = useTransform(progress, [start, start + 0.12], [28, 0]);
  const [markOn, setMarkOn] = useState(false);
  useMotionValueEvent(progress, "change", (v) => {
    if (stage.mark) setMarkOn(v > start + 0.1);
  });

  return (
    <div data-testid={`journey-step-${index + 1}`} className="py-4 md:py-6">
      {index > 0 && (
        <motion.span
          aria-hidden="true"
          style={{ opacity }}
          className="mb-3 block text-xl text-mirian-wine/30"
        >
          ↓
        </motion.span>
      )}
      <motion.div style={{ opacity, y }}>
        <p className="font-serif text-3xl uppercase leading-tight tracking-wide sm:text-4xl lg:text-5xl">
          {stage.mark ? (
            <span className={`mark-lime${markOn ? " mark-on" : ""}`}>{stage.label}</span>
          ) : (
            stage.label
          )}
        </p>
        {stage.desc && (
          <p className="mt-3 max-w-md text-sm italic text-mirian-wine/60 md:text-base">
            {stage.desc}
          </p>
        )}
      </motion.div>
    </div>
  );
}

const Static = () => (
  <ol data-testid="marmoles-journey" className="mt-4">
    {STAGES.map((stage, i) => (
      <li key={stage.label} data-testid={`journey-step-${i + 1}`}>
        <Reveal>
          <div className="py-7">
            {i > 0 && (
              <span aria-hidden="true" className="mb-3 block text-xl text-mirian-wine/30">
                ↓
              </span>
            )}
            <p className="font-serif text-3xl uppercase tracking-wide sm:text-4xl">
              {stage.mark ? <Mark>{stage.label}</Mark> : stage.label}
            </p>
            {stage.desc && (
              <p className="mt-3 text-sm italic text-mirian-wine/60">{stage.desc}</p>
            )}
          </div>
        </Reveal>
      </li>
    ))}
  </ol>
);

export default function Journey() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  if (reduce) return <Static />;

  return (
    <section
      ref={ref}
      data-testid="marmoles-journey"
      className="relative"
      style={{ height: "260vh" }}
      aria-label="De una idea a un proyecto que puede existir"
    >
      <div className="sticky top-0 flex h-screen items-center overflow-x-clip">
        <div className="mx-auto w-full max-w-3xl px-6 md:px-12">
          {STAGES.map((stage, i) => (
            <Stage key={stage.label} progress={scrollYProgress} index={i} stage={stage} />
          ))}
        </div>
      </div>
    </section>
  );
}
